import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable, from, of, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { OfflineQueueService } from "@/offline/offline-queue.service";
import { resolveEntityType } from "@/offline/response-shapes";

@Injectable({
    providedIn: 'root'
})
export class ApiService {

    private offlineQueue = inject(OfflineQueueService);

    constructor(private http: HttpClient) { }

    /**
     * Create/update. When offline (checked proactively, and reactively for the case
     * `navigator.onLine` said we had a connection but the request still couldn't reach the
     * backend - see ErrorInterceptor's `status === 0` passthrough), the write is queued in
     * IndexedDB instead of failing, and a response synthesized to look like the real
     * backend's is returned immediately so callers behave exactly as if the save succeeded.
     * A real 4xx/5xx from the server is never queued - only a genuine network failure is.
     */
    save(url: string, body: any, id: number | string = 0): Observable<any> {
        if (!navigator.onLine) {
            return from(this.offlineQueue.enqueueSave(url, body, id));
        }
        const request$ = id != 0 ? this.http.put(url + '/' + id, body) : this.http.post(url, body);
        return request$.pipe(
            catchError((err: HttpErrorResponse) => {
                if (err?.status === 0) {
                    return from(this.offlineQueue.enqueueSave(url, body, id));
                }
                return throwError(() => err);
            })
        );
    }

    getPetition(url: string, params: string = ''): Observable<any> {
        return this.http.get(url + params)
    }

    getById(url: string, id: number | string): Observable<any> {
        const numId = Number(id);
        if (numId < 0) {
            return this.resolveOfflineEntity(url, numId);
        }
        return this.http.get(url + '/' + id)
    }

    deleteById(url: string, id: number): Observable<any> {
        if (!navigator.onLine) {
            return from(this.offlineQueue.enqueueDelete(url, id));
        }
        return this.http.delete(url + '/' + id).pipe(
            catchError((err: HttpErrorResponse) => {
                if (err?.status === 0) {
                    return from(this.offlineQueue.enqueueDelete(url, id));
                }
                return throwError(() => err);
            })
        );
    }

    getSelectors(url: string, filters: any): Observable<any> {
        return this.http.post(url, filters);
    }

    /** Plain PUT with no id suffixing - for a fixed-path update endpoint like `users/me` that never takes an id in the URL (unlike `save`, which always appends one). Not offline-queued, same reasoning as `upload`. */
    put(url: string, body: any): Observable<any> {
        return this.http.put(url, body);
    }

    /** Plain fire-and-forget POST, no id suffixing and no offline queueing (unlike `save`) - for an action like "push a live sign request to a tablet" that only makes sense while actually online. */
    post(url: string, body: any): Observable<any> {
        return this.http.post(url, body);
    }

    /** Plain DELETE on a full url (no id suffixing) - for nested resources like `product-types/{id}/attributes/{attributeId}`. Not offline-queued, same as `put`/`post`. */
    delete(url: string): Observable<any> {
        return this.http.delete(url);
    }

    // Deliberately not offline-queued: uploads carry files (signatures, certificates, print
    // template assets) that are rare, settings-level actions rather than core "keep working
    // offline" flows, and storing file blobs in IndexedDB for later replay adds meaningful
    // complexity/risk for little payoff here.
    upload(url: string, formData: FormData, id: number = 0): Observable<any> {
        const endpoint = id ? `${url}/${id}` : url;
        return this.http.post(endpoint, formData);
    }

    getBlob(url: string): Observable<Blob> {
        return this.http.get(url, { responseType: 'blob' });
    }

    /** A temp (negative) id means this entity was created/edited offline and never round-tripped through the real backend. */
    private resolveOfflineEntity(url: string, tempId: number): Observable<any> {
        const entityType = resolveEntityType(url) ?? url;
        return from(this.offlineQueue.getRealIdForTemp(tempId)).pipe(
            switchMap(realId => {
                if (realId != null) {
                    // Already synced since this page loaded - fetch by the real id. Still goes
                    // through OfflineDataInterceptor, so it transparently falls back to the
                    // read cache if we're offline again by the time this runs.
                    return this.http.get(url + '/' + realId);
                }
                return from(this.offlineQueue.getQueuedEntity(entityType, tempId)).pipe(
                    switchMap(entity => entity
                        ? of(entity)
                        : throwError(() => ({ code: 404, status: 'error', message: 'No encontrado' })))
                );
            })
        );
    }

}